import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, shareReplay, throwError } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { Appointment, AppointmentFullInfo, AppointmentResponse, AsiponaCatalogs, AssignAppointment, DecByAppointmentResponse, PendingAppointmentsCount } from 'src/app/interfaces/appointments';
import { AuthService } from '../auth/auth.service';

/**
 * Servicio que gestiona las citas y solicitudes de cita de los clientes,
 * incluyendo su creación, asignación, cancelación, el envío a Asipona y la
 * obtención de conteos en tiempo real mediante SSE.
 */
@Injectable({
  providedIn: 'root'
})
export class AppointmentsService {
  private asiponaCatalogsCache$: Observable<AsiponaCatalogs[]> | null = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  /**
   * Obtiene el conteo de citas pendientes mediante SSE con autenticación.
   * Reconecta automáticamente con retroceso exponencial cuando el stream
   * se cierra o falla, manteniendo la suscripción activa indefinidamente
   * hasta que el consumidor se desuscriba.
   * @returns Observable que emite el conteo de citas pendientes en tiempo real.
   */
  getPendingAppointmentsSSE(): Observable<PendingAppointmentsCount> {
    return new Observable<PendingAppointmentsCount>(observer => {
      const url: string = `${GLOBAL.url}${GLOBAL.appointment.getPendingAppointments}`;

      let cancelled: boolean = false;
      let retryTimer: ReturnType<typeof setTimeout> | null = null;
      let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
      let attempt: number = 0;

      const BASE_DELAY_MS = 3000;
      const MAX_DELAY_MS = 30000;

      const startFetch = () => {
        if (cancelled) return;

        // Re-obtain token on every attempt to avoid stale-token failures after
        // long reconnection gaps
        const token = this.authService.getToken();
        const decoder = new TextDecoder();
        // Reset buffer for each new connection — leftover data from a previous
        // stream would corrupt the first event of the reconnected stream
        let buffer = '';

        fetch(url, {
          headers: {
            'Authorization': token,
            'Accept': 'text/event-stream'
          }
        }).then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          activeReader = response.body?.getReader() ?? null;

          const readStream = () => {
            activeReader?.read().then(({ done, value }) => {
              if (done) {
                if (!cancelled) {
                  const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
                  attempt++;
                  retryTimer = setTimeout(() => { if (!cancelled) startFetch(); }, delay);
                }
                return;
              }

              // Accumulate decoded bytes; stream:true handles multi-byte char boundaries
              buffer += decoder.decode(value, { stream: true });

              // Split on SSE event delimiter — only process complete events
              const parts = buffer.split('\n\n');
              buffer = parts.pop() ?? ''; // retain incomplete trailing fragment

              for (const part of parts) {
                for (const line of part.split('\n')) {
                  if (line.startsWith('data: ')) {
                    try {
                      const data = JSON.parse(line.substring(6));
                      observer.next(data);
                      attempt = 0; // reset backoff on successful event
                    } catch (error) {
                      console.error('Error parsing SSE data:', error);
                    }
                  }
                }
              }

              readStream();
            }).catch(error => {
              if (!cancelled) {
                const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
                attempt++;
                retryTimer = setTimeout(() => { if (!cancelled) startFetch(); }, delay);
              }
            });
          };

          readStream();
        }).catch(error => {
          if (!cancelled) {
            const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
            attempt++;
            retryTimer = setTimeout(() => { if (!cancelled) startFetch(); }, delay);
          }
        });
      };

      startFetch();

      return () => {
        cancelled = true;
        if (retryTimer !== null) clearTimeout(retryTimer);
        activeReader?.cancel();
      };
    });
  }

  /**
   * Obtiene todas las citas de una fecha, enriquecidas con descripciones de
   * estado y ordenadas de forma descendente por identificador.
   * @param date - Fecha de las citas a consultar.
   * @returns Observable con la lista de citas.
   */
  getAllAppointments(date: string): Observable<Appointment[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.listAllAppointments}/${date}`;
    return this.http.get<Appointment[]>(url).pipe(
      map(data => data.map(appointment => ({
        ...appointment,
        status_description: appointment.status === null ? 'Pendiente' :
          appointment.status === false ? 'No aceptada' : 'Aceptada',
        status_description_asipona: appointment.status_asipona === false ? 'No enviada' : appointment.status_asipona === true && appointment.cancellation_asipona === false ? 'Aceptada' : 'Cancelada',
      })).sort((a, b) => b.id - a.id))
    );
  }

  /**
   * Obtiene las solicitudes de cita del cliente identificado por el token,
   * con descripciones de estado y ordenadas de forma descendente.
   * @param date - Fecha de las solicitudes a consultar.
   * @returns Observable con la lista de solicitudes de cita.
   */
  getAppointmentsRequestByCustomer(date: string): Observable<Appointment[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.appointmentRequest.list}/${date}`;
    return this.http.get<Appointment[]>(url).pipe(
      map(data => data.map(appointment => ({
        ...appointment,
        status_description: appointment.status === null ? 'Pendiente' :
          appointment.status === false ? 'No aceptada' : 'Aceptada',
        status_description_asipona: appointment.status_asipona === false ? 'No enviada' : appointment.status_asipona === true && appointment.cancellation_asipona === false ? 'Aceptada' : 'Cancelada',
      })).sort((a, b) => b.id - a.id))
    );
  }

  /**
   * Obtiene una cita resolviendo el origen según la URL recibida.
   * @param id - Identificador de la cita.
   * @param date - Fecha de la cita.
   * @param url - URL que determina el tipo de consulta a realizar.
   * @returns Observable con la información completa de la cita.
   */
  getAppointment(id: number, date: string, url: string): Observable<AppointmentFullInfo[]> {
    if (url.includes('accept-cancel')) {
      return this.getAppointmentRequest(id, date);
    } else {
      return this.getAppointmentRequestbyCustomer(id, date);
    }
  }

  /**
   * Obtiene una solicitud de cita de un cliente con toda su información.
   * @param id - Identificador de la cita.
   * @param date - Fecha de la cita.
   * @returns Observable con la información completa de la cita.
   */
  getAppointmentRequestbyCustomer(id: number, date: string): Observable<AppointmentFullInfo[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.appointmentRequest.get}${date}/${id}`;
    return this.http.get<AppointmentFullInfo[]>(url);
  }

  /**
   * Obtiene una solicitud de cita con toda su información.
   * @param id - Identificador de la cita.
   * @param date - Fecha de la cita.
   * @returns Observable con la información completa de la cita.
   */
  getAppointmentRequest(id: number, date: string): Observable<AppointmentFullInfo[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.get}${date}/${id}`;
    return this.http.get<AppointmentFullInfo[]>(url);
  }

  /**
   * Crea una solicitud de cita de un cliente.
   * @param appointment - Datos de la cita a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createAppointmentRequest(appointment: Appointment): Observable<AppointmentResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.appointmentRequest.create}`;
    return this.http.post<AppointmentResponse>(url, appointment);
  }

  /**
   * Actualiza una solicitud de cita antes de ser aceptada o rechazada.
   * @param appointment - Datos de la cita a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateAppointmentRequest(appointment: Appointment): Observable<AppointmentResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.appointmentRequest.create}`;
    return this.http.put<AppointmentResponse>(url, appointment);
  }

  /**
   * Elimina una solicitud de cita antes de ser aceptada o rechazada.
   * @param id - Identificador de la cita a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteAppointmentRequest(id: number): Observable<AppointmentResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.appointmentRequest.delete}`;
    return this.http.delete<AppointmentResponse>(url, {
      headers: new HttpHeaders(),
      body: {
        id
      },
    });
  }

  /**
   * Cancela una solicitud de cita.
   * @param id - Identificador de la cita a cancelar.
   * @returns Observable con la respuesta de la cancelación.
   */
  cancelAppointmentRequest(id: number): Observable<any> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.cancel}`;
    return this.http.post<any>(url, {
      id
    });
  }

  /**
   * Cancela una cita previamente enviada a Asipona.
   * @param id - Identificador de la cita a cancelar.
   * @returns Observable con la respuesta de la cancelación.
   */
  cancelAppointmentInAsipona(id: number): Observable<any> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.cancelAsipona}`;
    return this.http.delete<any>(url, {
      headers: new HttpHeaders(),
      body: {
        id
      }
    });
  }

  /**
   * Envía una cita a Asipona.
   * @param id - Identificador de la cita a enviar.
   * @returns Observable con la respuesta del envío.
   */
  sendAppointmentToAsipona(id: number): Observable<any> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.sendAsipona}`;
    return this.http.post<any>(url, {
      id
    });
  }

  /**
   * Asigna una solicitud de cita.
   * @param appointment - Datos de la asignación de la cita.
   * @returns Observable con la respuesta de la asignación.
   */
  assignAppointmentRequest(appointment: AssignAppointment): Observable<AppointmentResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.assignAnAppointment}`;
    return this.http.put<AppointmentResponse>(url, appointment);
  }

  /**
   * Obtiene los catálogos de Asipona con caché en memoria, validando que
   * ninguno esté vacío.
   * @param forceRefresh - Si es `true`, ignora la caché y recarga los datos.
   * @returns Observable con la lista de catálogos de Asipona.
   */
  getAsiponaCatalogs(forceRefresh: boolean = false): Observable<AsiponaCatalogs[]> {
    if (!this.asiponaCatalogsCache$ || forceRefresh) {
      const url: string = `${GLOBAL.url}${GLOBAL.appointment.catalogs}`;
      this.asiponaCatalogsCache$ = this.http.get<AsiponaCatalogs[]>(url).pipe(
        map(catalogs => {
          const emptyCatalogs = catalogs.filter(
            catalog => !catalog.catalog_valor || catalog.catalog_valor.length === 0
          );

          if (emptyCatalogs.length > 0) {
            const emptyNames = emptyCatalogs.map(c => c.description).join(', ');
            throw new Error(`Los siguientes catálogos no tienen registros: ${emptyNames}`);
          }
          return catalogs;
        }),
        shareReplay(1)
      );
    }
    return this.asiponaCatalogsCache$;
  }
  /**
   * Obtiene la información DEC asociada a una cita.
   * @param appointmentId - Identificador de la cita.
   * @returns Observable con la información DEC de la cita.
   */
  getDecByAppointmentId(appointmentId: number): Observable<DecByAppointmentResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.appointment.getByAppointmentId}?appointment_id=${appointmentId}`;
    return this.http.get<DecByAppointmentResponse>(url);
  }

}
